/**
 * "Slow connection" banner (feature 334, research D7).
 *
 * Shown when the orders sync has had no success for 10 s, so a cook never trusts
 * a frozen board without knowing it. Small, at the bottom edge, and it lets
 * every tap through (pointer-events-none): it informs, it never gets in the way.
 * It re-reads the freshness once a second, so it also leaves on its own, within
 * a second of the next success.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "../i18n";
import type { Freshness } from "../lib/sync/freshness";

interface Props {
  freshness: Freshness;
  /** Injectable clock, for tests. */
  clock?: () => number;
}

const RECHECK_MS = 1_000;

export default function StaleBanner({ freshness, clock = Date.now }: Props) {
  const { t } = useTranslation();
  const [now, setNow] = useState(clock);

  useEffect(() => {
    const id = setInterval(() => setNow(clock()), RECHECK_MS);
    return () => clearInterval(id);
  }, [clock]);

  if (!freshness.isStale(now)) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed bottom-3 left-1/2 z-50 -translate-x-1/2 rounded-full bg-amber-500/90 px-4 py-1.5 text-kds-meta font-semibold text-black shadow"
    >
      {t("kds.sync.stale", "Connessione lenta, dati delle {{time}}", {
        time: freshness.formattedLastSuccess(),
      })}
    </div>
  );
}
